/**
 * DetectiveHub Page
 *
 * Browse detective stories, view stats, and select a story to read.
 * "Read like a writer" - analyzing story structure through quizzes.
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Search, Trophy, Target, Star, BookOpen, Loader2 } from 'lucide-react'
import { Button, Card, CardContent, Input } from '@/components/ui'
import { DetectiveStoryCard } from '@/components/detective'
import { useFamily } from '@/context/FamilyContext'
import {
  getStoriesWithAttemptStatus,
  getDetectiveStats,
  getDetectiveCategories,
  type StoryFilters,
} from '@/services/detectiveService'
import type { DetectiveStory, ChallengeDifficulty } from '@/types/database'

type StoryWithStatus = DetectiveStory & {
  hasAttempted: boolean
  bestScore: number | null
}

const DIFFICULTY_OPTIONS: { value: ChallengeDifficulty | 'all'; label: string }[] = [
  { value: 'all', label: 'All Levels' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
]

export function DetectiveHub() {
  const navigate = useNavigate()
  const { activeWriter } = useFamily()

  const [stories, setStories] = useState<StoryWithStatus[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [stats, setStats] = useState({
    storiesCompleted: 0,
    totalScore: 0,
    perfectScores: 0,
    averageScore: 0,
  })

  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [difficulty, setDifficulty] = useState<ChallengeDifficulty | 'all'>('all')
  const [category, setCategory] = useState('all')

  // Load data
  useEffect(() => {
    async function loadData() {
      if (!activeWriter) return

      setIsLoading(true)
      try {
        const filters: StoryFilters = {}
        if (difficulty !== 'all') filters.difficulty = difficulty
        if (category !== 'all') filters.category = category

        const [storiesData, statsData, categoriesData] = await Promise.all([
          getStoriesWithAttemptStatus(activeWriter.id, filters),
          getDetectiveStats(activeWriter.id),
          getDetectiveCategories(),
        ])

        setStories(storiesData)
        setStats(statsData)
        setCategories(categoriesData)
      } catch (error) {
        console.error('Error loading detective data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [activeWriter, difficulty, category])

  // Filter by search
  const filteredStories = stories.filter(story =>
    story.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    story.author.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Separate featured and regular stories
  const featuredStories = filteredStories.filter(s => s.is_featured)
  const regularStories = filteredStories.filter(s => !s.is_featured)

  const handleStoryClick = (storyId: string) => {
    navigate(`/detective/${storyId}`)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--color-kids-bg)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-kids-primary)]" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)] pb-20">
      {/* Header */}
      <header className="bg-gradient-to-r from-[var(--color-kids-accent)] to-[var(--color-kids-primary)] text-white">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-3 mb-2">
              <Search className="w-8 h-8" />
              <h1 className="font-display font-bold text-2xl">Story Detective</h1>
            </div>
            <p className="text-white/80">
              Read like a writer! Find the story elements hidden in each tale.
            </p>
          </motion.div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 -mt-4">
        {/* Stats Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="mb-6 shadow-lg">
            <CardContent className="p-4">
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="w-10 h-10 mx-auto rounded-full bg-[var(--color-kids-success)]/10 flex items-center justify-center mb-1">
                    <BookOpen className="w-5 h-5 text-[var(--color-kids-success)]" />
                  </div>
                  <p className="font-bold text-lg text-[var(--color-kids-text)]">
                    {stats.storiesCompleted}
                  </p>
                  <p className="text-xs text-[var(--color-kids-text-muted)]">
                    Stories Read
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 mx-auto rounded-full bg-[var(--color-kids-primary)]/10 flex items-center justify-center mb-1">
                    <Target className="w-5 h-5 text-[var(--color-kids-primary)]" />
                  </div>
                  <p className="font-bold text-lg text-[var(--color-kids-text)]">
                    {stats.totalScore}
                  </p>
                  <p className="text-xs text-[var(--color-kids-text-muted)]">
                    Total Points
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 mx-auto rounded-full bg-[var(--color-kids-accent)]/10 flex items-center justify-center mb-1">
                    <Star className="w-5 h-5 text-[var(--color-kids-accent)]" />
                  </div>
                  <p className="font-bold text-lg text-[var(--color-kids-text)]">
                    {stats.perfectScores}
                  </p>
                  <p className="text-xs text-[var(--color-kids-text-muted)]">
                    Perfect
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 mx-auto rounded-full bg-[var(--color-kids-secondary)]/10 flex items-center justify-center mb-1">
                    <Trophy className="w-5 h-5 text-[var(--color-kids-secondary)]" />
                  </div>
                  <p className="font-bold text-lg text-[var(--color-kids-text)]">
                    {stats.averageScore}
                  </p>
                  <p className="text-xs text-[var(--color-kids-text-muted)]">
                    Avg Score
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6 space-y-4"
        >
          {/* Search */}
          <Input
            type="search"
            placeholder="Search stories..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full"
          />

          {/* Difficulty and Category filters */}
          <div className="flex gap-3 overflow-x-auto pb-2">
            {/* Difficulty */}
            <div className="flex gap-2">
              {DIFFICULTY_OPTIONS.map(option => (
                <Button
                  key={option.value}
                  variant={difficulty === option.value ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setDifficulty(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Category filter */}
          {categories.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-2">
              <Button
                variant={category === 'all' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setCategory('all')}
              >
                All Categories
              </Button>
              {categories.map(cat => (
                <Button
                  key={cat}
                  variant={category === cat ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setCategory(cat)}
                >
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </Button>
              ))}
            </div>
          )}
        </motion.div>

        {/* Featured Stories */}
        {featuredStories.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8"
          >
            <h2 className="font-display font-bold text-lg text-[var(--color-kids-text)] mb-3">
              Featured Stories
            </h2>
            <div className="space-y-3">
              {featuredStories.map(story => (
                <DetectiveStoryCard
                  key={story.id}
                  story={story}
                  hasAttempted={story.hasAttempted}
                  bestScore={story.bestScore}
                  onClick={() => handleStoryClick(story.id)}
                />
              ))}
            </div>
          </motion.section>
        )}

        {/* All Stories */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="font-display font-bold text-lg text-[var(--color-kids-text)] mb-3">
            {featuredStories.length > 0 ? 'More Stories' : 'All Stories'}
          </h2>

          {regularStories.length > 0 ? (
            <div className="space-y-3">
              {regularStories.map(story => (
                <DetectiveStoryCard
                  key={story.id}
                  story={story}
                  hasAttempted={story.hasAttempted}
                  bestScore={story.bestScore}
                  onClick={() => handleStoryClick(story.id)}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <div className="text-4xl mb-3">🔍</div>
                <h3 className="font-display font-bold text-[var(--color-kids-text)] mb-1">
                  No stories found
                </h3>
                <p className="text-sm text-[var(--color-kids-text-muted)]">
                  {searchQuery
                    ? 'Try a different search term'
                    : 'New stories coming soon!'}
                </p>
              </CardContent>
            </Card>
          )}
        </motion.section>
      </main>
    </div>
  )
}

export default DetectiveHub
